import { useState, useMemo } from 'react';

import { PropTypes } from 'prop-types';

import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { formatCentsAsDollars } from '@zenbusiness/graphql-commons-utils/formatters/currency';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { FEDERATED } from '#client/build-env';
import Trustpilot from '#client/components/Trustpilot';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import { getEntityTypeLabelFromValue } from '#client/utils/businessInfo';
import { cleanPlanServiceName } from '#client/utils/product';

import { CHECKOUT } from '../../../../constants';
import { CheckoutCard } from '../../../CheckoutCard';
import { EditProductModal } from '../../../EditProductModal';
import TaxesCallout from '../../../TaxesCallout';

/** @typedef {import('@zenbusiness/application-commons-ecommerce/dist/types').CartItem} CartItem */
/**
 * Assigns a render flag to all items in the provided list and handles specific conditions,
 * it also checks if a service name alias should be assigned to the item and calculates
 * the display amount for each item, including special cases like combined BOF pricing.
 *
 * @param {Array<object>} items - The list of items to process.
 * @param {string} items[].serviceName - The name of the service for each item.
 * @param {boolean} [items[].render] - The render flag for each item.
 * @param {object} [items[].serviceAmount] - The price of the service (money type object).
 * @param {object} [items[].couponAmount] - The discount amount from coupons (money type object).
 * @param {string} [items[].displayAmount] - The calculated display price (added by this function).
 * @param {string} entityType - The type of entity for the business.
 * @returns {Array<object>} The updated list of items with render flags and display amounts.
 */
/* eslint-disable no-param-reassign */
function assignRenderFlagAndServiceNameAliasToItems(items, entityType) {
  // assign render flag to all items
  const cartItems = items.map((item) => {
    item.render = true;

    // Get values from money type objects
    const serviceAmountValue = item.serviceAmount?.amount || 0;
    const couponAmountValue = item.couponAmount?.amount || 0;

    // Calculate the amount in dollars (not cents)
    const displayAmountValue = couponAmountValue
      ? serviceAmountValue - couponAmountValue
      : serviceAmountValue;

    // Format the price for display
    item.displayAmount = formatCentsAsDollars({
      cents: displayAmountValue,
      trimWholeDollars: true
    });

    return item;
  });

  // handle ongoing and bof items
  const ongoingBof = cartItems.find((item) => item.serviceName === 'ongoing_bof_compliance');
  if (ongoingBof) {
    const bof = cartItems.find((item) => item.serviceName === 'beneficial_ownership_filing');
    if (bof) {
      bof.render = false;

      // Get values from money type objects
      const ongoingBofAmount = ongoingBof.serviceAmount?.amount || 0;
      const ongoingBofCouponAmount = ongoingBof.couponAmount?.amount || 0;
      const oneTimeBofAmount = bof.serviceAmount?.amount || 0;
      const oneTimeBofCouponAmount = bof.couponAmount?.amount || 0;

      // Calculate the combined price in dollars (not cents)
      const combinedAmount =
        ongoingBofAmount - ongoingBofCouponAmount + (oneTimeBofAmount - oneTimeBofCouponAmount);

      // Format the price for display
      ongoingBof.displayAmount = formatCentsAsDollars({
        cents: combinedAmount,
        trimWholeDollars: true
      });
    }
  }

  // handle s-corp designation
  const sCorpDesignation = cartItems.find((item) => item.serviceName === 's_corp_designation');
  if (sCorpDesignation) {
    sCorpDesignation.render = false;
  }

  // handle operating agreement for corporations
  const operatingAgreement = cartItems.find((item) => item.serviceName === 'operating_agreement');
  if (operatingAgreement && entityType === 'corp') {
    // a serviceNameAlias is a prop that tells a service can be called by a different name
    operatingAgreement.serviceNameAlias = 'corporate_bylaws';
  }

  return cartItems;
}

/**
 * Combine cart items with the same serviceName into a single object.
 * This ensures that services with different jurisdictionStates are grouped together.
 * Prevents rendering duplicated items in the completed step.
 * @param {Array<CartItem>} cartItems - List of cart items
 * @returns {Array<CartItem>} List of grouped cart items
 */
const groupServices = (cartItems) => {
  const groupedCartItemsMap = new Map();

  cartItems.forEach((item) => {
    const { serviceName, serviceAmount, couponAmount, feesAmount } = item;

    if (groupedCartItemsMap.has(serviceName)) {
      const existingItem = groupedCartItemsMap.get(serviceName);

      // Sum money type object amounts
      if (existingItem.serviceAmount !== undefined && serviceAmount !== undefined) {
        existingItem.serviceAmount.amount += serviceAmount?.amount || 0;
      }

      if (existingItem.feesAmount !== undefined && feesAmount !== undefined) {
        existingItem.feesAmount.amount += feesAmount?.amount || 0;
      }

      if (existingItem.couponAmount !== undefined || couponAmount !== undefined) {
        if (!existingItem.couponAmount) {
          existingItem.couponAmount = { amount: 0, currency: 'USD' };
        }
        existingItem.couponAmount.amount += couponAmount?.amount || 0;
      }
    } else {
      // Create a new item with consistent money type objects
      const newItem = { ...item };

      // Ensure consistent money type objects
      if (!newItem.serviceAmount) {
        newItem.serviceAmount = { amount: 0, currency: 'USD' };
      }

      if (!newItem.couponAmount) {
        newItem.couponAmount = { amount: 0, currency: 'USD' };
      }

      if (!newItem.feesAmount) {
        newItem.feesAmount = { amount: 0, currency: 'USD' };
      }

      groupedCartItemsMap.set(serviceName, newItem);
    }
  });

  return Array.from(groupedCartItemsMap.values());
};

function CartCheckoutCard({ onContinue }) {
  const contents = useCartContents();
  const { items: cartItems } = contents;
  const [editProductUuid, setEditProductUuid] = useState(null);
  const { isPrimary } = useFlowConfig();
  const { businessInfo } = useFlowState();

  const { entityType } = businessInfo;
  const entityTypeLabel = getEntityTypeLabelFromValue(entityType);

  const items = useMemo(() => {
    const groupedItems = groupServices(cartItems);
    return assignRenderFlagAndServiceNameAliasToItems(groupedItems, entityType).reduce(
      (acc, item) => {
        if (!item.render) {
          return acc;
        }
        if (item.isDeferred) {
          acc.dueLater.push(item);
        } else {
          acc.dueToday.push(item);
        }
        return acc;
      },
      { dueToday: [], dueLater: [] }
    );
  }, [cartItems, entityType]);

  const getDisplayProductName = (item) => {
    // clean up plan service names ex: 'starter_plan_v27' -> 'starter_plan'
    const serviceNameKey = item.serviceNameAlias || item.serviceName;
    const cleanedServiceName = cleanPlanServiceName(serviceNameKey);
    return (
      <FormattedMessage
        id={`product.name.${cleanedServiceName}`}
        values={{ formationType: entityTypeLabel }}
      />
    );
  };

  const handleProductModalClose = () => {
    setEditProductUuid(null);
  };

  return (
    <>
      <CheckoutCard step={CHECKOUT.STEP.CART}>
        <CheckoutCard.Active>
          <FederatedModule
            exportName="CartItemsWidget"
            isPrimary={isPrimary}
            purchaseSource="flow"
            silent
            // TODO: edit product functionality is not ready yet, so we are disabling it for now
            // onEdit={handleProductModalOpen}
            {...FEDERATED.EcommerceUi}
          />
          <Stack spacing={3}>
            <Button
              clickstreamProps={{
                elementName: 'checkout_card_continue',
                elementValue: CHECKOUT.STEP.CART
              }}
              data-testid={`checkout-card-continue-${CHECKOUT.STEP.CART}`}
              fullWidth
              onClick={onContinue}
            >
              <FormattedMessage id="shopping.page.checkout.continue" />
            </Button>
            <Box display={{ xs: 'block', md: 'none' }}>
              <Trustpilot />
            </Box>
            <Box display={{ xs: 'block', md: 'none' }}>
              <TaxesCallout />
            </Box>
          </Stack>
        </CheckoutCard.Active>
        <CheckoutCard.Complete>
          <Stack spacing={1.5}>
            <Stack spacing={0.5}>
              {items?.dueToday.map((item) => (
                <Stack
                  key={item.serviceName}
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Text size="xs">{getDisplayProductName(item)}</Text>
                  <Text size="xs">{item.displayAmount}</Text>
                </Stack>
              ))}
              {items?.dueLater.map((item) => (
                <Stack
                  key={item.serviceName}
                  alignItems="start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Text size="xs">
                    {getDisplayProductName(item)}
                    {item.displayAmount !== '$0' && (
                      <Text
                        color="secondary"
                        component="span"
                        display={{ xs: 'block', md: 'inline' }}
                        size="xs"
                      >
                        <Box component="span" display={{ xs: 'none', md: 'inline' }}>
                          {' - '}
                        </Box>
                        <FormattedMessage id="shopping.page.checkout.cart.deferred" />
                      </Text>
                    )}
                  </Text>
                  <Text size="xs">{item.displayAmount}</Text>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </CheckoutCard.Complete>
        <CheckoutCard.Incomplete />
      </CheckoutCard>
      <EditProductModal
        open={Boolean(editProductUuid)}
        productUuid={editProductUuid}
        onClose={handleProductModalClose}
      />
    </>
  );
}

CartCheckoutCard.propTypes = {
  onContinue: PropTypes.func.isRequired
};

export default CartCheckoutCard;
